/* eslint-disable @typescript-eslint/no-explicit-any */
import { Select } from 'antd';

import { LanguageImg } from 'components/ossix/LangComponent';
import agbPlatformText from 'constants/platformLabels/agbPlatformText';
import { useLanguage } from 'hooks/Language/useLanguage';
import useOssixProviders from 'hooks/useOssixProviders';
import { IOssixProvider } from 'interfaces/otherInterface/dynamicInput';
import React, { CSSProperties, useEffect, useState } from 'react';
import { ActionType } from 'types/generalType';
import { CURRENT_PLATFORM } from 'utils/config';

const { Option } = Select;

export interface Payload {
  ActionType?: ActionType;
  CountryCode?: string;
  TargetType?: string;
  IncludeSelf?: string;
}

interface OssixProvidersDropdownProps {
  showDefaultValue?: boolean;
  defaultValue?: string;
  style?: CSSProperties | undefined;
  className?: string | undefined;
  payload: Payload;
  selectedProvider?: IOssixProvider | undefined;
  getSelectedProvider?: React.Dispatch<
    React.SetStateAction<IOssixProvider | undefined>
  >;
  beneficiaryData?: any;
  setSelectedBeneficiary?: any;
  onCloseSelectContactDropdown?: () => void;
  disabled?: boolean;
}

const OssixProvidersDropdown: React.FC<
  OssixProvidersDropdownProps
> = ({
  showDefaultValue = false,
  defaultValue = undefined,
  style,
  className,
  payload,
  getSelectedProvider,
  selectedProvider,
  beneficiaryData,
  setSelectedBeneficiary,
  onCloseSelectContactDropdown,
  disabled = false,
}) => {
  const { translate } = useLanguage();
  const [value, setValue] = useState(defaultValue);
  const [isDefault, setIsDefault] = useState(showDefaultValue);
  const [defaultValueData, setDefaultValueData] = useState<any>();
  const { providers, loadingProviders } = useOssixProviders({
    ActionType: payload.ActionType,
    CountryCode: payload.CountryCode,
    IncludeSelf: payload.IncludeSelf,
    TargetType: payload.TargetType,
  });

  const findProvider = React.useCallback(
    (searchValue: any) => {
      if (!searchValue || !providers) return undefined;
      const searchStr = String(searchValue).toLowerCase();
      return providers.find(
        (item: IOssixProvider) =>
          String(item.ShortName).toLowerCase() === searchStr ||
          String(item.ID).toLowerCase() === searchStr ||
          String(item.OperatorID).toLowerCase() === searchStr,
      );
    },
    [providers],
  );

  const onHandleChangeValue = (newValue: string) => {
    setSelectedBeneficiary?.(undefined);
    onCloseSelectContactDropdown?.();
    const filteredProvider = findProvider(newValue);
    setIsDefault(false);
    setValue(newValue);
    if (filteredProvider?.ShortName !== selectedProvider?.ShortName) {
      getSelectedProvider?.(filteredProvider);
    }
  };

  useEffect(() => {
    if (loadingProviders || !providers?.length) return;

    // Resolve name from props or local state
    const nameFromProp = findProvider(selectedProvider?.ShortName)?.ShortName;
    const nameFromBenef = findProvider(beneficiaryData?.ShortName)?.ShortName;
    const nameFromLocal = findProvider(value)?.ShortName;

    let targetProvider = findProvider(nameFromProp || nameFromBenef || nameFromLocal);

    if (!targetProvider && isDefault) {
      targetProvider = providers.find(
        (item: IOssixProvider) =>
          String(item?.ShortName).toUpperCase().trim() === 'AGB',
      );
    }

    if (targetProvider) {
      const targetName = targetProvider.ShortName;
      
      // Update local value state for Select display
      if (value !== targetName) {
        setValue(targetName);
      }

      // Update parent only if it has a different or unresolved value
      if (
        targetName !== selectedProvider?.ShortName ||
        (!selectedProvider?.OperatorName &&
          targetProvider.OperatorName) ||
        (!selectedProvider?.Logo && targetProvider.Logo)
      ) {
        getSelectedProvider?.(targetProvider);
      }
    }
  }, [
    loadingProviders,
    providers, 
    beneficiaryData?.ShortName,
    isDefault,
    // dependencies carefully chosen to avoid loops while ensuring sync on data arrival
  ]);

  return (
    <Select
      showSearch
      loading={loadingProviders}
      placeholder={translate('Operator')}
      optionFilterProp="children"
      onChange={onHandleChangeValue}
      size="large"
      value={
        isDefault && !loadingProviders
          ? value || defaultValueData?.ShortName
          : (!loadingProviders && (value || selectedProvider?.ShortName)) ||
            undefined
      }
      style={style}
      className={className}
      disabled={disabled}
    >
      {providers?.map(
        (provider: IOssixProvider, index: number) => (
          <Option key={index} value={provider?.ShortName}>
            <span className="display-row-layout align-row-center">
              <LanguageImg
                width={30}
                flag={provider?.Logo}
                name={provider?.OperatorName}
              />
              <span>{provider?.OperatorName}</span>
            </span>
          </Option>
        ),
      )}
    </Select>
  );
};

export default OssixProvidersDropdown;
